import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Eu } from '@sgc/contracts';
import { get, post } from './api';

export interface Auth {
  carregando: boolean;
  setupNecessario: boolean;
  eu: Eu | null;
  recarregar: () => Promise<void>;
  /** devolve o desafio quando a conta tem 2FA (a sessão só abre em validar2fa) */
  entrar: (email: string, senha: string) => Promise<{ desafio: string } | null>;
  validar2fa: (desafio: string, codigo: string) => Promise<void>;
  setup: (d: { escritorio: string; nome: string; email: string; senha: string }) => Promise<void>;
  sair: () => Promise<void>;
  trocarEscritorio: (id: string) => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [carregando, setCarregando] = useState(true);
  const [setupNecessario, setSetup] = useState(false);
  const [eu, setEu] = useState<Eu | null>(null);

  const recarregar = useCallback(async () => {
    try {
      const e = await get<{ setupNecessario: boolean; eu: Eu | null }>('/v1/auth/estado');
      setSetup(e.setupNecessario);
      setEu(e.eu);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
    const semSessao = () => setEu(null);
    window.addEventListener('sgc:sem-sessao', semSessao);
    return () => window.removeEventListener('sgc:sem-sessao', semSessao);
  }, [recarregar]);

  const valor = useMemo<Auth>(() => ({
    carregando, setupNecessario, eu, recarregar,
    async entrar(email, senha) {
      const r = await post<Eu | { precisa2fa: true; desafio: string }>('/v1/auth/login', { email, senha });
      if ('precisa2fa' in r) return { desafio: r.desafio };
      setEu(r); return null;
    },
    async validar2fa(desafio, codigo) { setEu(await post<Eu>('/v1/auth/2fa/validar', { desafio, codigo })); },
    async setup(d) { setEu(await post<Eu>('/v1/auth/setup', d)); setSetup(false); },
    async sair() { await post('/v1/auth/logout'); setEu(null); },
    async trocarEscritorio(id) { await post('/v1/auth/escritorio', { escritorioId: id }); await recarregar(); },
  }), [carregando, setupNecessario, eu, recarregar]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const a = useContext(Ctx);
  if (!a) throw new Error('useAuth fora do AuthProvider');
  return a;
}
